import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, TIER_NAMES, TIER_COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { fetchMembershipTiers, fetchSubscription, createPaymentCheckout } from '../services/api';
import { Crown, ArrowLeft, CheckCircle2, Compass, BarChart2, TrendingUp, Diamond, RefreshCw, Loader2 } from 'lucide-react';

const TIER_ICONS = [Compass, BarChart2, TrendingUp, Diamond];

// Maps tier number → server-defined Stripe package id (defined in routes/payments.py)
const TIER_TO_PACKAGE = {
  2: 'tier_2_monthly',
  3: 'tier_3_monthly',
  4: 'tier_4_monthly',
};

export default function MembershipPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchMembershipTiers().then(setTiers).catch(() => {}),
      fetchSubscription().then(setSubscription).catch(() => {}),
    ]).then(() => setLoading(false));
  }, []);

  const startCheckout = async (tier) => {
    setErr(null);
    if (tier.price === 0) {
      navigate('/dashboard');
      return;
    }
    const pkg = TIER_TO_PACKAGE[tier.tier];
    if (!pkg) {
      setErr('This tier is not available for checkout yet.');
      return;
    }
    setBusyTier(tier.tier);
    try {
      const { url } = await createPaymentCheckout({
        package_id: pkg,
        origin_url: window.location.origin,
      });
      if (!url) throw new Error('No checkout URL returned');
      window.location.href = url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Checkout failed');
      setBusyTier(null);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textSecondary }}>Loading plans...</div>;

  return (
    <div data-testid="membership-page" style={{ padding: '24px 20px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
        <Crown size={26} color={COLORS.primary} />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Membership Plans</h1>
      </div>

      {subscription && subscription.subscription_active && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, backgroundColor: '#E8F5E9', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <CheckCircle2 size={22} color={COLORS.success} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>Active — {subscription.tier_name}</div>
            <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>{subscription.days_remaining} days remaining · ${subscription.price}/mo</div>
          </div>
        </div>
      )}

      <h2 style={{ fontSize: 28, fontWeight: 800, color: COLORS.textPrimary, textAlign: 'center', fontFamily: 'Outfit, sans-serif' }}>Choose Your Plan</h2>
      <p style={{ fontSize: 15, color: COLORS.textSecondary, textAlign: 'center', marginTop: 6, marginBottom: 24 }}>Unlock powerful health optimization tools</p>

      {err && (
        <div style={{ backgroundColor: '#FFEBEE', color: COLORS.error, padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14, textAlign: 'center' }}>{err}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {tiers.map((tier, i) => {
          const isCurrent = (user?.membership_tier || 0) === tier.tier;
          const color = TIER_COLORS[tier.tier] || '#999';
          const Icon = TIER_ICONS[i] || Compass;
          const busy = busyTier === tier.tier;
          return (
            <div key={i} data-testid={`membership-tier-${tier.tier}`} style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 24, border: isCurrent ? `2px solid ${color}` : `1px solid ${COLORS.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', position: 'relative' }}>
              {isCurrent && <span style={{ position: 'absolute', top: -10, left: 20, padding: '4px 12px', borderRadius: 9999, backgroundColor: color, fontSize: 12, fontWeight: 700, color: '#FFF' }}>Current Plan</span>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={28} color={color} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{tier.name}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, marginTop: 2 }}>{tier.price === 0 ? 'Free' : `$${tier.price}`}{tier.price > 0 && <span style={{ fontSize: 14, fontWeight: 400, color: COLORS.textSecondary }}>/month</span>}</div>
                </div>
              </div>
              <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, marginBottom: 16 }}>{tier.description}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {tier.features.map((f, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8 }}><CheckCircle2 size={16} color={color} /><span style={{ fontSize: 14, color: COLORS.textPrimary }}>{f}</span></div>
                ))}
              </div>
              {!isCurrent && (
                <button
                  data-testid={`subscribe-tier-${tier.tier}-btn`}
                  onClick={() => startCheckout(tier)}
                  disabled={busy}
                  style={{ width: '100%', padding: '16px', borderRadius: 9999, border: 'none', backgroundColor: color, color: '#FFF', fontSize: 16, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.75 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                  {tier.price === 0 ? 'Start Free' : busy ? 'Redirecting to Stripe…' : `Subscribe for $${tier.price}/mo`}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
